/**
 * ReadinessIssuesPanel - Issues gruppiert nach Typ
 *
 * Unterstuetzt Issue-Selektion zum Filtern der Produkttabelle.
 * Enthaelt KI-Referenz-Filter (Packshot-Status) und Bild-/Text-Probleme.
 */

import { AlertTriangle, XSquare, Diamond, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useReadinessIssues } from '@/hooks/useChannelReadiness'
import type { IssueFilter, IssueGroup, ImageIntelligenceCounts } from '@/lib/api/channel-readiness'
import { ReadinessIssueGroup } from './ReadinessIssueGroup'

interface ReadinessIssuesPanelProps {
  channelId: string | null
  selectedIssue?: IssueFilter | null
  onSelectIssue?: (issue: IssueFilter | null) => void
  imageStatusFilter?: string[]
  onImageStatusFilter?: (filter: string[]) => void
}

function isGroupActive(group: IssueGroup, selected: IssueFilter | null | undefined): boolean {
  if (!selected || selected.missing_packshot) return false
  return (
    group.issue_type === selected.issue_type &&
    group.field === selected.field &&
    group.message === selected.message
  )
}

type NumericCountKey = 'no_images_count' | 'candidate_count' | 'no_good_candidate_count' | 'packshot_assigned_count' | 'not_analyzed_count'

const STATUS_ROWS: Array<{
  status: string
  icon: typeof XSquare
  iconColor: string
  labelKey: string
  countKey: NumericCountKey
}> = [
  { status: 'no_images', icon: XSquare, iconColor: 'text-red-500', labelKey: 'readiness.noImage', countKey: 'no_images_count' },
  { status: 'has_candidate', icon: Diamond, iconColor: 'text-primary-500', labelKey: 'readiness.candidate', countKey: 'candidate_count' },
  { status: 'no_candidate', icon: AlertTriangle, iconColor: 'text-amber-500', labelKey: 'readiness.noCandidate', countKey: 'no_good_candidate_count' },
  { status: 'packshot_assigned', icon: CheckCircle2, iconColor: 'text-emerald-500', labelKey: 'readiness.assigned', countKey: 'packshot_assigned_count' },
]

export function ReadinessIssuesPanel({
  channelId,
  selectedIssue,
  onSelectIssue,
  imageStatusFilter,
  onImageStatusFilter,
}: ReadinessIssuesPanelProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useReadinessIssues(channelId)
  const issues = data?.issues ?? []
  const intelligence = data?.image_intelligence as ImageIntelligenceCounts | null

  if (!channelId) return null

  const imageIssues = issues.filter((i) => i.issue_type === 'image')
  const textIssues = issues.filter((i) => i.issue_type === 'text')
  const totalKiRef = intelligence
    ? intelligence.no_images_count + intelligence.candidate_count + intelligence.no_good_candidate_count + intelligence.packshot_assigned_count
    : 0
  const hasAnyContent = issues.length > 0 || totalKiRef > 0

  const handleClick = (group: IssueGroup) => {
    if (!onSelectIssue) return
    if (isGroupActive(group, selectedIssue)) {
      onSelectIssue(null)
    } else {
      onImageStatusFilter?.([])
      onSelectIssue({ issue_type: group.issue_type, field: group.field, message: group.message })
    }
  }

  const handleStatusClick = (status: string) => {
    if (imageStatusFilter?.includes(status)) {
      onImageStatusFilter?.([])
    } else {
      onSelectIssue?.(null)
      onImageStatusFilter?.([status])
    }
  }

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {t('readiness.issues')}
        </h3>
      </div>

      {isLoading ? (
        <div className="text-sm text-neutral-500 py-4 text-center">{t('common.loading')}</div>
      ) : !hasAnyContent ? (
        <div className="text-sm text-neutral-500 py-4 text-center">{t('readiness.noIssues')}</div>
      ) : (
        <div className="space-y-4">
          {intelligence && totalKiRef > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">
                {t('readiness.aiReference')} ({totalKiRef} Prod.)
              </h4>
              <div className="space-y-0.5">
                {STATUS_ROWS.map((row) => {
                  const count = intelligence[row.countKey]
                  if (!count) return null
                  const Icon = row.icon
                  const active = imageStatusFilter?.includes(row.status)
                  return (
                    <StatusFilterRow
                      key={row.status}
                      icon={<Icon className={`h-4 w-4 flex-shrink-0 ${row.iconColor}`} />}
                      label={t(row.labelKey)}
                      count={count}
                      active={active}
                      onClick={() => handleStatusClick(row.status)}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {imageIssues.length > 0 && (
            <IssueSection
              title={`${t('readiness.missingImages')} (${imageIssues.reduce((s, i) => s + i.product_count, 0)})`}
              groups={imageIssues}
              selectedIssue={selectedIssue}
              onClick={handleClick}
            />
          )}

          {textIssues.length > 0 && (
            <IssueSection
              title={`Text (${textIssues.reduce((s, i) => s + i.product_count, 0)})`}
              groups={textIssues}
              selectedIssue={selectedIssue}
              onClick={handleClick}
            />
          )}
        </div>
      )}
    </div>
  )
}

function StatusFilterRow({ icon, label, count, active, onClick }: {
  icon: React.ReactNode; label: string; count: number; active?: boolean; onClick: () => void
}) {
  const cls = active
    ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200 dark:ring-primary-800'
    : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
  return (
    <div
      role="button" tabIndex={0} className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer transition-colors ${cls}`}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate">{label}</span>
      </div>
      <span className={`text-sm font-medium tabular-nums ml-3 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
        {count} Prod.
      </span>
    </div>
  )
}

function IssueSection({ title, groups, selectedIssue, onClick }: {
  title: string; groups: IssueGroup[]; selectedIssue?: IssueFilter | null; onClick: (g: IssueGroup) => void
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">{title}</h4>
      <div className="space-y-0.5">
        {groups.map((group, idx) => (
          <ReadinessIssueGroup key={idx} group={group} isActive={isGroupActive(group, selectedIssue)} onClick={onClick} />
        ))}
      </div>
    </div>
  )
}
